import { useState } from 'react';
import {
  Box,
  Button,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Flex,
  Skeleton,
  Spinner,
  Text,
} from '@optiaxiom/react';
import { IconCaretDownSolid, IconCaretUpSolid } from '@optimizely/axiom-icons';

import { MarkdownText } from '@frontend/components/MarkdownText';
import { transformChunks } from '@frontend/features/agents/utils/memoryChunkUtils';
import { MemoryChunk } from '@frontend/types/specialized-agent';

import { EmptyStateMessage } from './EmptyStateMessage';
import { ToolCallDisplay } from './ToolCallDisplay';

type MemoryChunksDisplayProps = {
  memoryChunks: MemoryChunk[];
  isLoading?: boolean;
  isStreaming?: boolean;
  emptyMessage?: string;
  emptySubMessage?: string;
  chunkCountLabel?: string;
};

export function MemoryChunksDisplay({
  memoryChunks,
  isLoading = false,
  isStreaming = false,
  emptyMessage = 'No memory chunks available.',
  emptySubMessage,
  chunkCountLabel = 'Memory chunks',
}: MemoryChunksDisplayProps) {
  const [expandAll, setExpandAll] = useState(false);
  const [disclosureStates, setDisclosureStates] = useState<Record<number, boolean>>({});
  const [nestedInputStates, setNestedInputStates] = useState<Record<number, boolean>>({});
  const [nestedOutputStates, setNestedOutputStates] = useState<Record<number, boolean>>({});

  const transformedChunks = transformChunks(memoryChunks);

  const handleToggleAll = () => {
    const newExpanded = !expandAll;
    setExpandAll(newExpanded);

    // Update all individual disclosure states
    const newStates: Record<number, boolean> = {};
    const newInputStates: Record<number, boolean> = {};
    const newOutputStates: Record<number, boolean> = {};

    transformedChunks.forEach((_, index) => {
      newStates[index] = newExpanded;
      newInputStates[index] = newExpanded;
      newOutputStates[index] = newExpanded;
    });

    setDisclosureStates(newStates);
    setNestedInputStates(newInputStates);
    setNestedOutputStates(newOutputStates);
  };

  const handleDisclosureChange = (index: number, open: boolean) => {
    setDisclosureStates(prev => ({
      ...prev,
      [index]: open,
    }));
  };

  const handleInputDisclosureChange = (index: number, open: boolean) => {
    setNestedInputStates(prev => ({
      ...prev,
      [index]: open,
    }));
  };

  const handleOutputDisclosureChange = (index: number, open: boolean) => {
    setNestedOutputStates(prev => ({
      ...prev,
      [index]: open,
    }));
  };
  if (isLoading) {
    return (
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py="24"
        gap="8"
        bg="bg.secondary"
        rounded="md"
      >
        <Spinner size="sm" />
        <Text color="fg.secondary">Loading memory chunks...</Text>
      </Flex>
    );
  }

  if (!isStreaming && (!memoryChunks || memoryChunks.length === 0)) {
    return <EmptyStateMessage message={emptyMessage} subMessage={emptySubMessage} />;
  }

  return (
    <Flex flexDirection="column" gap="12">
      <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
        <Text fontSize="sm" color="fg.secondary">
          {chunkCountLabel} ({transformedChunks.length})
        </Text>
        <Button
          appearance="subtle"
          size="sm"
          onClick={handleToggleAll}
          icon={expandAll ? <IconCaretUpSolid /> : <IconCaretDownSolid />}
        >
          {expandAll ? 'Collapse All' : 'Expand All'}
        </Button>
      </Flex>
      {transformedChunks.map((chunk, index) => {
        if (chunk.type === 'TOOL call' && chunk.inputContent && chunk.outputContent) {
          return (
            <ToolCallDisplay
              key={chunk.id}
              toolName={chunk.toolName || 'Unknown Tool'}
              inputContent={chunk.inputContent}
              outputContent={chunk.outputContent}
              open={disclosureStates[index] ?? false}
              onOpenChange={open => handleDisclosureChange(index, open)}
              inputOpen={nestedInputStates[index] ?? false}
              onInputOpenChange={open => handleInputDisclosureChange(index, open)}
              outputOpen={nestedOutputStates[index] ?? false}
              onOutputOpenChange={open => handleOutputDisclosureChange(index, open)}
            />
          );
        }

        return (
          <Disclosure
            key={chunk.id}
            open={disclosureStates[index] ?? false}
            onOpenChange={open => handleDisclosureChange(index, open)}
          >
            <Box border="1" p="4" borderColor="border.default" rounded="md">
              <DisclosureTrigger>
                <Box p="4" w="full">
                  <Flex flexDirection="row" justifyContent="flex-start" alignItems="center">
                    <Box w="64">
                      <Text fontSize="sm" fontWeight="600" color="fg.default">
                        {chunk.type}
                      </Text>
                    </Box>
                    <Box w="full">
                      <Text fontSize="sm" color="fg.secondary" lineClamp="1">
                        {chunk.content.substring(0, 200)}
                        {chunk.content.length > 200 ? '...' : ''}
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              </DisclosureTrigger>
              <DisclosureContent>
                <Box p="12" bg="bg.default">
                  <MarkdownText text={chunk.content} />
                </Box>
              </DisclosureContent>
            </Box>
          </Disclosure>
        );
      })}
      {isStreaming && <Skeleton h="80" />}
    </Flex>
  );
}
